import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import moment from "moment";
import { useDispatch } from "react-redux";

import ApiService from "../../services/api-service";
import notifier from "../Notifier";
import { getErrorString, truncate } from "../../constants/function";
import { addSongAction } from "../../action/songs";
import { getPlayableUrl } from "../../utils/songUrl";
import { hostDayLabel, submissionSummary, UNSCHEDULED_DAY } from "./jukeboxSchedule";

const gold = "#FFEE20";
const goldBright = "#F5DC6A";
const textPrimary = "#E8E8EE";
const textMuted = "#9A9AAB";

const STATUS_STYLE = {
  pending: { label: "Pending", color: gold },
  accepted: { label: "Accepted", color: "#4CAF50" },
  played: { label: "Played", color: "#64B5F6" },
  rejected: { label: "Rejected", color: "#FF6B6B" },
};
const TIER_LABEL = { fol: "Front of Line", skip: "Skip the Line", free: "Free", custom: "Offer" };
const PAGE_SIZE = 50;

/**
 * Host view of what fans submitted for each jukebox day, one tab per day plus
 * "Unscheduled / live". Pending requests can be accepted or rejected with the
 * same endpoints as the host's pending list. Controlled via { open, onClose };
 * `initialSlotId` picks the first tab.
 */
export default function JukeboxDaySubmissions({ open, onClose, initialSlotId }) {
  const dispatch = useDispatch();
  const [days, setDays] = useState([]);
  const [unscheduled, setUnscheduled] = useState(null);
  const [slotId, setSlotId] = useState(initialSlotId || null);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingDays, setLoadingDays] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [actingId, setActingId] = useState(null);

  const loadDays = useCallback(async () => {
    const res = await ApiService.SONG_REQUEST_HOST_DAYS();
    const result = res?.result || {};
    setDays(Array.isArray(result.days) ? result.days : []);
    setUnscheduled(result.unscheduled || null);
    return result;
  }, []);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoadingDays(true);
    loadDays()
      .then((result) => {
        if (!active) return;
        // Start on the requested day, else the first day, else unscheduled.
        const ids = (result.days || []).map((d) => d.id);
        setSlotId(ids.includes(initialSlotId) ? initialSlotId : ids[0] || UNSCHEDULED_DAY);
      })
      .catch((error) => active && notifier.error(getErrorString(error)))
      .finally(() => active && setLoadingDays(false));
    return () => {
      active = false;
    };
  }, [open, initialSlotId, loadDays]);

  const loadItems = useCallback(async (id, cursor) => {
    setLoadingItems(true);
    try {
      const res = await ApiService.SONG_REQUEST_HOST_DAY(id, { limit: PAGE_SIZE, cursor });
      const page = res?.result?.items || [];
      setItems((prev) => (cursor ? [...prev, ...page] : page));
      setNextCursor(res?.result?.nextCursor || null);
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setLoadingItems(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !slotId) return;
    setItems([]);
    loadItems(slotId);
  }, [open, slotId, loadItems]);

  // Refresh the list and the tab counts after acting on a request.
  const refresh = async () => {
    await Promise.all([loadItems(slotId), loadDays().catch(() => {})]);
  };

  const handleAccept = async (item) => {
    setActingId(item.id);
    try {
      await ApiService.SONG_REQUEST_ACCEPT(item.id);
      notifier.success("Request accepted");
      await refresh();
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (item) => {
    setActingId(item.id);
    try {
      await ApiService.SONG_REQUEST_REJECT(item.id);
      notifier.success("Request rejected and refunded");
      await refresh();
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setActingId(null);
    }
  };

  // Preview through the app's own player, like any other song.
  const preview = (song) => dispatch(addSongAction(song));

  const tabs = [...days, ...(unscheduled ? [unscheduled] : [])];
  const current = tabs.find((d) => d.id === slotId);

  const renderItem = (item) => {
    const status = STATUS_STYLE[item.status] || STATUS_STYLE.pending;
    const playable = Boolean(getPlayableUrl(item.song));
    const busy = actingId === item.id;
    return (
      <Stack
        key={item.id}
        direction="row"
        alignItems="center"
        spacing={1.25}
        data-testid="day-submission"
        sx={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: "12px",
          px: 1.25,
          py: 1,
        }}
      >
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Avatar
            src={item.song.thumbnailImg?.filePath || undefined}
            variant="rounded"
            sx={{ width: 44, height: 44, borderRadius: "8px" }}
          />
          {playable && (
            <IconButton
              onClick={() => preview(item.song)}
              size="small"
              aria-label={`Play ${item.song.title}`}
              sx={{
                position: "absolute",
                inset: 0,
                m: "auto",
                width: 28,
                height: 28,
                color: "#0A0A0A",
                background: "rgba(255, 238, 32,0.9)",
                "&:hover": { background: gold },
              }}
            >
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography noWrap sx={{ fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "13px", color: textPrimary }}>
            {truncate(item.song.title, 40)}
          </Typography>
          <Typography noWrap sx={{ fontFamily: "var(--font-body)", fontSize: "11px", color: textMuted }}>
            {item.song.artist}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" mt={0.25}>
            <Avatar src={item.requester.avatar || undefined} sx={{ width: 16, height: 16 }} />
            <Typography noWrap sx={{ fontFamily: "var(--font-body)", fontSize: "11px", color: textMuted }}>
              {item.requester.displayName} · {moment(item.submittedAt).fromNow()}
            </Typography>
          </Stack>
        </Box>
        <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
          <Typography sx={{ fontFamily: "var(--font-body)", fontWeight: 700, fontSize: "12px", color: gold }}>
            {item.tipAmount} SIKI
          </Typography>
          <Typography sx={{ fontFamily: "var(--font-body)", fontSize: "10px", color: textMuted }}>
            {TIER_LABEL[item.tier] || "Free"}
          </Typography>
          {item.status === "pending" ? (
            <Stack direction="row" spacing={0.25}>
              <Tooltip title={item.live ? "Accept" : "Start streaming first"}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleAccept(item)}
                    disabled={!item.live || busy}
                    aria-label={`Accept ${item.song.title}`}
                    sx={{ color: "#4CAF50" }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject and refund">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleReject(item)}
                    disabled={busy}
                    aria-label={`Reject ${item.song.title}`}
                    sx={{ color: "#f44336" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ) : (
            <Typography sx={{ fontFamily: "var(--font-body)", fontWeight: 700, fontSize: "10px", color: status.color }}>
              {status.label}
              {item.status === "rejected" && item.refunded ? " · refunded" : ""}
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          background: "linear-gradient(180deg, #0C0C12 0%, #08080C 100%)",
          color: textPrimary,
          border: "1px solid rgba(255, 238, 32,0.18)",
          borderRadius: "16px",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ height: "1px", background: "linear-gradient(90deg, transparent, rgba(255, 238, 32,0.6), transparent)" }} />
      <DialogTitle
        sx={{
          fontFamily: "var(--font-display)",
          fontSize: "22px",
          letterSpacing: "0.5px",
          color: gold,
          textTransform: "uppercase",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 0.5,
        }}
      >
        Submissions
        <IconButton onClick={onClose} size="small" sx={{ color: textMuted }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {loadingDays && tabs.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} sx={{ color: gold }} />
        </Box>
      ) : (
        <>
          <Tabs
            value={tabs.some((d) => d.id === slotId) ? slotId : false}
            onChange={(_, value) => setSlotId(value)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Jukebox days"
            sx={{
              px: 2,
              minHeight: 40,
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              "& .MuiTabs-indicator": { backgroundColor: gold },
              "& .MuiTab-root": {
                fontFamily: "var(--font-body)",
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 600,
                color: textMuted,
                minHeight: 40,
              },
              "& .MuiTab-root.Mui-selected": { color: goldBright },
            }}
          >
            {tabs.map((day) => (
              <Tab key={day.id} value={day.id} label={hostDayLabel(day)} />
            ))}
          </Tabs>
          <DialogContent sx={{ pt: 1.5, minHeight: 240 }}>
            {current && (
              <Typography sx={{ fontFamily: "var(--font-body)", color: textMuted, fontSize: "11px", mb: 1.25 }}>
                {current.description ? `${current.description} · ` : ""}
                {submissionSummary(current.counts)}
                {current.status === "closed" ? " · closed" : ""}
              </Typography>
            )}
            {loadingItems && items.length === 0 ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={24} sx={{ color: gold }} />
              </Box>
            ) : items.length === 0 ? (
              <Typography sx={{ fontFamily: "var(--font-body)", color: textMuted, fontSize: "13px", py: 3, textAlign: "center" }}>
                No submissions for this day yet.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {items.map(renderItem)}
                {nextCursor && (
                  <Button
                    onClick={() => loadItems(slotId, nextCursor)}
                    disabled={loadingItems}
                    sx={{ fontFamily: "var(--font-body)", color: gold, textTransform: "none", fontSize: "12px" }}
                  >
                    Load more
                  </Button>
                )}
              </Stack>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}
